import { PAY_SYMBOL_BY_ADDRESS } from "@/contract/constants";
import {
  fetchAllCardsFromChain,
  metadataUriFromCardInfo,
  nftProxyFromCardInfo,
} from "@/contract/funding";
import { fromTokenWei, getTokenDecimals } from "@/contract/tokens";
import { getReadWeb3 } from "@/contract/index";
import { fetchCardMetadata, resolveNftContractFromMeta } from "@/film/api/metadata";

export function chainProjectIdForIndex(index) {
  return `idx-${index}`;
}

function weiToNumber(wei, decimals) {
  return Number(fromTokenWei(wei, decimals));
}

function buildAbout(description) {
  const text = (description || "").trim();
  return { zh: text, en: text };
}

async function mapInfoToProject(info, index, web3, decimalsCache) {
  if (!info || Number(info.currentPrice) === 0) return null;

  const payToken = info.payToken;
  const payKey = payToken.toLowerCase();
  let decimals = decimalsCache.get(payKey);
  if (decimals === undefined) {
    decimals = await getTokenDecimals(payToken, web3);
    decimalsCache.set(payKey, decimals);
  }
  const coin = PAY_SYMBOL_BY_ADDRESS[payKey];
  if (!coin) {
    throw new Error(`Unsupported pay token: ${payToken}`);
  }

  const incrementWei = info.increment;
  const mode = Number(incrementWei) === 0 ? "fixed" : "step";
  const initHuman = weiToNumber(info.initPrice, decimals);
  const incHuman = weiToNumber(incrementWei, decimals);
  const currentHuman = weiToNumber(info.currentPrice, decimals);

  let meta = {};
  const uri = metadataUriFromCardInfo(info);
  if (uri) {
    try {
      meta = await fetchCardMetadata(uri);
    } catch (err) {
      console.warn("fetchCardMetadata failed", index, uri, err);
    }
  }

  const cardAddress =
    resolveNftContractFromMeta(meta) || nftProxyFromCardInfo(info);
  const fallbackName = info.admin
    ? `${String(info.admin).slice(0, 6)}…${String(info.admin).slice(-4)}`
    : `#${index + 1}`;
  const name = meta.name || meta.project_name || fallbackName;
  const issuer = meta.issuer || meta.creator || info.admin || "";
  const description = meta.description || "";
  const id = cardAddress ? cardAddress.toLowerCase() : chainProjectIdForIndex(index);

  const project = {
    id,
    cardAddress: cardAddress || "",
    name,
    nameEn: meta.name_en || meta.english || name,
    issuer,
    coin,
    payToken,
    tokenDecimals: decimals,
    mode,
    supply: Number(info.supply),
    sold: Number(info.collectAmount),
    collectGoalWei: info.collectGoal,
    isFinish: Boolean(info.isFinish),
    currentPriceWei: info.currentPrice,
    currentPriceHuman: currentHuman,
    tokenUri: uri,
    imageUrl: meta.imageUrl || "",
    english: meta.english || meta.tagline || "",
    posterBottom: meta.poster_bottom || null,
    about: buildAbout(description),
    about2: buildAbout(meta.about2 || meta.disclaimer || ""),
    displayIndex: index,
    chainIndex: index,
  };

  if (mode === "fixed") {
    project.fixed = initHuman;
  } else {
    project.step = { start: initHuman, inc: incHuman };
  }

  return project;
}

export async function fetchChainProjects() {
  const entries = await fetchAllCardsFromChain();
  const web3 = getReadWeb3();
  const decimalsCache = new Map();
  const projects = [];

  for (let i = 0; i < entries.length; i += 1) {
    const { info, index } = entries[i];
    const project = await mapInfoToProject(info, index, web3, decimalsCache);
    if (project) projects.push(project);
  }

  return projects;
}

export function findChainProject(projects, id) {
  if (!id) return null;
  const key = id.toLowerCase();
  return (
    projects.find(
      (p) =>
        p.id === key ||
        (p.cardAddress && p.cardAddress.toLowerCase() === key) ||
        chainProjectIdForIndex(p.chainIndex) === key
    ) || null
  );
}
